import {
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  Input,
  Radio,
  Select,
  Space,
  Table,
  Typography,
  message,
} from "antd";
import {
  DownOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  UpOutlined,
  LoadingOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import http from "@/utils/http";
import api from "@/utils/api";
import OwnerGroupPicker from "./OwnerGroupPicker";
import "./index.css";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const GROUP_OPTIONS = [
  { label: "No grouping", value: "" },
  { label: "Owner", value: "owner" },
  { label: "Trust", value: "trust" },
  { label: "Group", value: "group" },
  { label: "Supervisor", value: "supervisor" },
];
const STATUS_OPTIONS = [
  { label: "All except inactive zero balances", value: "exclude" },
  { label: "All properties", value: "all" },
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];
const DEFAULT_GL_ACCT = [
  "401,601-6049999,375-3759999,605-6069999,608-6139999,616-6169999,621-6299999,",
  "635-6429999,390,607-6079999,615-6159999,617-6199999,643-6799999,614-6149999,",
  "620-6209999,630-6349999,730-7309999,734-7349999,738-7399999,750-7529999,",
  "790-7909999,714-7149999,715-7159999,753-759999,741-7499999,768-7799999,",
  "783-7899999,791-7949999,760-7609999,761-7619999,762-7639999,764-7649999,",
  "765-7679999,780-7829999,797-7999999,800-8039999,815-8999999,712-7129999,",
  "726-7289999,795-7969999,998-9999999,735-7379999,733-7339999,731-7329999,",
  "740-7409999,701-7019999,702-7029999,703-7039999,704-7049999,705-7059999,",
  "706-7069999,707-7079999,708-7089999,709-7099999,710-7109999,713-7139999,",
  "720-7259999,729-7299999",
].join("");

const money = (value) => {
  const amount = Number(value || 0);
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(Math.abs(amount));
  return amount < 0 ? `(${formatted})` : formatted;
};
const amountClass = (value) =>
  Number(value || 0) < 0
    ? "one-line-amount-negative"
    : "one-line-amount-positive";
const listFrom = (response) =>
  Array.isArray(response) ? response : response?.data || [];

const defaultFormValues = () => {
  const today = dayjs();

  const currentRange = [today.startOf("month"), today];
  return {
    reportType: 1,
    currentDateRange: currentRange,
    lastYearDateRange: [
      today.subtract(1, "year").startOf("year"),
      today.subtract(1, "year").endOf("year"),
    ],
    groupBy: "",
    printByGroup: false,
    propertyStatus: "exclude",
    isFreeClear: "",
    glAcct: DEFAULT_GL_ACCT,
    supervisorIdList: [],
    groupIdList: [],
    trustIdList: [],
    ownerIdList: [],
    propTypeList: [],
  };
};

/** Dashboard report page for the Java replacement of PHP One Line Cash Flow. */
const OneLineCashFlowReport = () => {
  const [form] = Form.useForm();
  const [reportType, setReportType] = useState(1);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [selectOptions, setSelectOptions] = useState({});
  const [ownerGroupId, setOwnerGroupId] = useState(null);
  const [tableHeight, setTableHeight] = useState(300);
  const tableHost = useRef(null);
  const loadedOptions = useRef({});

  // Changing the current range renews the default previous-year equivalent.
  const syncLastYearRange = (range) => {
    if (!range?.[0] || !range?.[1]) return;

    const previousYear = range[1].subtract(1, "year");

    form.setFieldsValue({
      lastYearDateRange: [
        previousYear.startOf("year"),
        previousYear.endOf("year"),
      ],
    });
  };

  useEffect(() => {
    form.setFieldsValue(defaultFormValues());
  }, [form]);

  // Size only the table body to the remaining preview area, including after filters collapse.
  useEffect(() => {
    const host = tableHost.current;
    if (!host) return undefined;
    const resize = () => {
      const header = host.querySelector(".ant-table-header");
      setTableHeight(
        Math.max(80, host.clientHeight - (header?.offsetHeight || 54) - 4),
      );
    };
    const observer = new ResizeObserver(resize);
    observer.observe(host);
    const header = host.querySelector(".ant-table-header");
    if (header) observer.observe(header);
    resize();
    return () => observer.disconnect();
  }, [report, filtersCollapsed]);

  const loadOptions = async (key) => {
    if (loadedOptions.current[key]) return;
    const definitions = {
      supervisors: [
        api.getSupervisorSOList,
        (x) => ({
          value: x.value,
          label: [x.label?.fullName, x.label?.groupCodes]
            .filter(Boolean)
            .join(" - "),
        }),
      ],
      groups: [
        api.commonGroupList,
        (x) => ({
          value: x.groupId,
          label: x.geographyGroup || x.groupCode || x.group,
        }),
      ],
      trusts: [
        api.getTrustDictList,
        (x) => ({ value: x.trustId, label: x.trustNameTitle || x.trust }),
      ],
      owners: [
        api.getOwnerDictList,
        (x) => ({ value: x.ownerId, label: x.titleName || x.ownerName }),
      ],
      propTypes: [
        api.getPropTypeList,
        (x) => ({ value: x.code, label: x.name || x.code }),
      ],
    };
    try {
      const [url, map] = definitions[key];
      const options = listFrom(await http.get(url))
        .map(map)
        .filter((x) => x.value !== undefined && x.value !== null);
      setSelectOptions((previous) => ({ ...previous, [key]: options }));
      loadedOptions.current[key] = true;
    } catch (_error) {
      message.warning("Some filter options could not be loaded.");
    }
  };

  // Convert UI values to the validated Java JSON contract.
  const buildPayload = (values) => {
    const [currentFrom, currentTo] = values.currentDateRange || [];
    const [lastFrom, lastTo] = values.lastYearDateRange || [];
    const numberOrNull = (value) => {
      if (value === undefined || value === null || value === "") return null;
      const parsed = Number(String(value).replace(/[,$\s]/g, ""));
      return Number.isFinite(parsed) ? Math.abs(parsed) : null;
    };
    return {
      reportType: values.reportType,
      currentDateFrom: currentFrom?.format("YYYY-MM-DD"),
      currentDateTo: currentTo?.format("YYYY-MM-DD"),
      lastYearDateFrom:
        values.reportType === 2 ? lastFrom?.format("YYYY-MM-DD") : null,
      lastYearDateTo:
        values.reportType === 2 ? lastTo?.format("YYYY-MM-DD") : null,
      groupBy: values.groupBy || "",
      printByGroup: Boolean(values.printByGroup),
      prop: values.prop?.trim() || "",
      supervisorIdList: values.supervisorIdList || [],
      groupIdList: values.groupIdList || [],
      trustIdList: values.trustIdList || [],
      ownerIdList: values.ownerIdList || [],
      propTypeList: values.propTypeList || [],
      isFreeClear: values.isFreeClear || "",
      propertyStatus: values.propertyStatus || "exclude",
      glAcct: values.glAcct?.trim() || "",
      incomeGreaterThan: numberOrNull(values.incomeGreaterThan),
      incomeLessThan: numberOrNull(values.incomeLessThan),
      lossGreaterThan: numberOrNull(values.lossGreaterThan),
      lossLessThan: numberOrNull(values.lossLessThan),
    };
  };
  const validatedPayload = async () =>
    buildPayload(await form.validateFields());

const resetReport = () => {
  runControllerRef.current?.abort();
  runIdRef.current += 1;
  form.resetFields();
  form.setFieldsValue(defaultFormValues());
  setOwnerGroupId(null);
  setReportType(1);
  setReport(null);
  setLoading(false);
};
  const runControllerRef = useRef(null);
  const runIdRef = useRef(0);
 const runReport = async () => {
  let payload;
  try {
    payload = await validatedPayload();
  } catch (_validationError) {
    return;
  }

  runControllerRef.current?.abort(); // 中途重新点击时，先中断上一个还在飞的请求
  const controller = new AbortController();
  runControllerRef.current = controller;
  const thisRunId = ++runIdRef.current;

  setLoading(true);
  try {
    const response = await http.post(api.getOneLineCashFlowShow, payload, {
      signal: controller.signal,
    });

    if (response === null) return; // 被取消的请求，静默忽略
    if (thisRunId !== runIdRef.current) return; // 兜底防止旧响应覆盖新结果

    if (response?.code !== "1") throw new Error(response?.msg || "Unable to run the report.");
    setReport(response.data);
  } catch (error) {
    if (thisRunId !== runIdRef.current) return;
    message.error(error?.message || "Please review the report filters.");
  } finally {
    if (thisRunId === runIdRef.current) setLoading(false);
  }
};
  const exportReport = async (format) => {
    setExporting(format);
    try {
      const endpoint =
        format === "pdf"
          ? api.getOneLineCashFlowToPdf
          : api.getOneLineCashFlowToCsv;
      const blob = await http.post(endpoint, await validatedPayload(), {
        responseType: "blob",
      });
      if (!(blob instanceof Blob)) throw new Error("Invalid file response");
      // Global error responses can arrive as a JSON Blob even for a binary request.
      if (blob.type.includes("application/json"))
        throw new Error(JSON.parse(await blob.text())?.msg || "Export failed");
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `One_Line_Cash_Flow_${dayjs().format("YYYYMMDD_HHmmss")}.${format}`;
      link.click();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      message.error(error?.message || "Export failed.");
    } finally {
      setExporting("");
    }
  };

  // Keep the period list referentially stable for the dynamic-column memo below.
  const periods = useMemo(() => report?.periods || [], [report]);
  const columns = useMemo(() => {
    const groupCell = (record, value) =>
      record?.rowType === "GROUP_HEADER"
        ? { children: value, props: { colSpan: 6 + periods.length } }
        : value;
    const hiddenCell = (record, value) =>
      record?.rowType === "GROUP_HEADER"
        ? { children: value, props: { colSpan: 0 } }
        : value;
    const leading = [
      {
        title: "Property - Address",
        dataIndex: "propertyAddress",
        width: 240,
        render: (value, record) =>
          groupCell(record, record?.groupLabel || value),
      },
      {
        title: "City - State",
        dataIndex: "cityState",
        width: 135,
        render: (value, record) => hiddenCell(record, value),
      },
      {
        title: "Units",
        dataIndex: "units",
        width: 72,
        align: "center",
        render: (value, record) => hiddenCell(record, value || "-"),
      },
      {
        title: "P. Type",
        dataIndex: "propType",
        width: 90,
        render: (value, record) => hiddenCell(record, value),
      },
      {
        title: "V.",
        dataIndex: "vacancy",
        width: 55,
        align: "center",
        render: (value, record) => hiddenCell(record, value || "-"),
      },
      {
        title: "E.",
        dataIndex: "eviction",
        width: 55,
        align: "center",
        render: (value, record) => hiddenCell(record, value || "-"),
      },
    ];
    return leading.concat(
      periods.map((period) => ({
        title: period.label,
        dataIndex: ["amounts", period.key],
        width: 142,
        align: "right",
        render: (value, record) =>
          record?.rowType === "GROUP_HEADER" ? (
            { children: value, props: { colSpan: 0 } }
          ) : (
            <span className={amountClass(value)}>{money(value)}</span>
          ),
      })),
    );
  }, [periods]);

  const multiSelect = (name, label, key) => (
    <Form.Item name={name} label={label}>
      <Select
        mode="multiple"
        allowClear
        showSearch
        optionFilterProp="label"
        options={selectOptions[key] || []}
        onDropdownVisibleChange={(open) => open && loadOptions(key)}
      />
    </Form.Item>
  );

  // 日期
  const [presetYear, setPresetYear] = useState(dayjs().year());
  const rangePresets = (() => {
    const today = dayjs();
    const monthPresets = Array.from({ length: 12 }, (_, index) => {
      const month = dayjs().year(presetYear).month(index);
      return {
        label: month.format("MMMM"),
        value: [month.startOf("month"), month.endOf("month")],
      };
    });

    return [
      {
        label: (
          <span
            style={{ fontWeight: 600, color: "#666", pointerEvents: "none" }}
          >
            Quick Select
          </span>
        ),
        value: null,
      },
      { label: "Today", value: [today, today] },
      {
        label: "Yesterday",
        value: [today.subtract(1, "day"), today.subtract(1, "day")],
      },
      { label: "Last 7 Days", value: [today.subtract(6, "day"), today] },
      { label: "Last 30 Days", value: [today.subtract(29, "day"), today] },
      {
        label: "This Month",
        value: [today.startOf("month"), today.endOf("month")],
      },
      {
        label: "Last Month",
        value: [
          today.subtract(1, "month").startOf("month"),
          today.subtract(1, "month").endOf("month"),
        ],
      },
      {
        label: (
          <span
            className="preset-group-title"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            Months ({presetYear})
          </span>
        ),
        value: null,
      },
      ...monthPresets,
    ];
  })();

  return (
    <main className="one-line-cash-flow-page">
      <section
        className={`one-line-layout${filtersCollapsed ? " one-line-layout-filters-collapsed" : ""}`}
      >
        <Card
          className="one-line-filter-card"
          style={filtersCollapsed ? { display: "none" } : undefined}
          title={
            <div className="one-line-filter-header">
              <div className="one-line-filter-title-row">
                <span>Report filters</span>
                <Button
                  className="one-line-collapse-button"
                  type="text"
                  icon={<UpOutlined />}
                  onClick={() => setFiltersCollapsed(true)}
                >
                  Hide filters
                </Button>
              </div>
              <div className="one-line-filter-actions">
                <Button icon={<ReloadOutlined />} onClick={resetReport}>
                  Reset
                </Button>
             <Button
             type="primary"
             icon={loading ? <LoadingOutlined /> : <PlayCircleOutlined />}
             onClick={runReport}
             >
              Run Report
             </Button>
              </div>
            </div>
          }
        >
          <Form
            form={form}
            layout="vertical"
            onValuesChange={(changed, values) => {
              if (changed.reportType) setReportType(changed.reportType);
              if (changed.currentDateRange)
                syncLastYearRange(values.currentDateRange);
              if (Object.prototype.hasOwnProperty.call(changed, "ownerIdList"))
                setOwnerGroupId(null);
            }}
          >
            <Form.Item
              name="reportType"
              label="Report Type"
              rules={[{ required: true }]}
            >
              <Radio.Group className="one-line-type-group">
                <Radio.Button value={1}>
                  <span className="one-line-type-label">Current Income Loss</span>
                </Radio.Button>
                <Radio.Button value={2}>
                  <span className="one-line-type-label">5 Years Income Loss</span>
                </Radio.Button>
              </Radio.Group>
            </Form.Item>
            {/* <Form.Item name="currentDateRange" label="Current Year Date Range" rules={[{ required: true, message: "Select a current date range" }]}><RangePicker style={{ width: "100%" }} /></Form.Item>
            {reportType === 2 && <Form.Item name="lastYearDateRange" label="Last Year Date Range" rules={[{ required: true, message: "Select a last-year date range" }]}><RangePicker style={{ width: "100%" }} /></Form.Item>} */}
            <Form.Item
              name="currentDateRange"
              label="Current Year Date Range"
              rules={[
                { required: true, message: "Select a current date range" },
              ]}
            >
              <RangePicker
                style={{ width: "100%" }}
                presets={rangePresets}
                onPanelChange={(values) => {
                  if (values?.[0]) setPresetYear(values[0].year());
                }}
              />
            </Form.Item>
            {reportType === 2 && (
              <Form.Item
                name="lastYearDateRange"
                label="Last Year Date Range"
                rules={[
                  { required: true, message: "Select a last-year date range" },
                ]}
              >
                <RangePicker
                  style={{ width: "100%" }}
                  presets={rangePresets}
                  onPanelChange={(values) => {
                    if (values?.[0]) setPresetYear(values[0].year());
                  }}
                />
              </Form.Item>
            )}
            <Form.Item name="groupBy" label="Group By">
              <Select options={GROUP_OPTIONS} />
            </Form.Item>
            <Form.Item name="prop" label="Property">
              <Input.TextArea
                className="one-line-property-input"
                rows={3}
                placeholder="e.g. 1001, 1005-1010"
              />
            </Form.Item>
            <Form.Item name="isFreeClear" label="Free & Clear">
              <Select
                allowClear
                options={[
                  { label: "Yes", value: "Yes" },
                  { label: "No", value: "No" },
                ]}
              />
            </Form.Item>
            {multiSelect("ownerIdList", "Owner", "owners")}
            <OwnerGroupPicker
              value={ownerGroupId}
              ownerOptions={selectOptions.owners || []}
              loadOwners={() => loadOptions("owners")}
              onSelect={(group) => {
                setOwnerGroupId(group?.id ?? null);
                form.setFieldsValue({ ownerIdList: group?.ownerIds || [] });
                loadOptions("owners");
              }}
            />
            {multiSelect("supervisorIdList", "Supervisor", "supervisors")}
            {multiSelect("groupIdList", "Group", "groups")}
            {multiSelect("trustIdList", "Trust", "trusts")}
            {multiSelect("propTypeList", "Property Type", "propTypes")}
            <Form.Item name="propertyStatus" label="Property Status">
              <Select options={STATUS_OPTIONS} />
            </Form.Item>
            <Form.Item name="incomeGreaterThan" label="Income Greater Than">
              <Input prefix="$" inputMode="decimal" />
            </Form.Item>
            <Form.Item name="incomeLessThan" label="Income Less Than">
              <Input prefix="$" inputMode="decimal" />
            </Form.Item>
            <Form.Item name="lossGreaterThan" label="Loss Greater Than">
              <Input prefix="$" inputMode="decimal" />
            </Form.Item>
            <Form.Item name="lossLessThan" label="Loss Less Than">
              <Input prefix="$" inputMode="decimal" />
            </Form.Item>
            <Form.Item name="printByGroup" label="PDF Layout">
              <Select
                options={[
                  { label: "Continuous", value: false },
                  { label: "Start each group on a new page", value: true },
                ]}
              />
            </Form.Item>
            <Form.Item name="glAcct" label="GL Account">
              <Input.TextArea autoSize={{ minRows: 3, maxRows: 7 }} />
            </Form.Item>
          </Form>
        </Card>
        <div className="one-line-report-column">
          {filtersCollapsed && (
            <div className="one-line-show-filters">
              <Button
                className="one-line-collapse-button"
                type="text"
                icon={<DownOutlined />}
                onClick={() => setFiltersCollapsed(false)}
              >
                Show filters
              </Button>
            </div>
          )}
          <Card
            className="one-line-result-card"
            title="Report preview"
            extra={
              <Space>
                <Button
                  icon={<FileTextOutlined />}
                  loading={exporting === "csv"}
                  onClick={() => exportReport("csv")}
                >
                  CSV
                </Button>
                <Button
                  type="primary"
                  icon={<FilePdfOutlined />}
                  loading={exporting === "pdf"}
                  onClick={() => exportReport("pdf")}
                >
                  PDF
                </Button>
              </Space>
            }
          >
            <div className="one-line-result-toolbar">
              <Text className="one-line-result-subtitle">
                {report
                  ? [
                      `${report.propertyCount || 0} properties`,
                      periods[0]?.label,
                    ]
                      .filter(Boolean)
                      .join(" · ")
                  : "Run the report to review data before exporting."}
              </Text>
            </div>
            {report ? (
              <div ref={tableHost} className="one-line-table-host">
                <Table
                  className="one-line-table"
                  rowKey={(record, index) =>
                    `${record.rowType}-${record.propId || record.groupLabel || index}`
                  }
                  columns={columns}
                  dataSource={report.rows || []}
                  loading={loading}
                  pagination={false}
                  size="small"
                  scroll={{ x: "max-content", y: tableHeight }}
                  rowClassName={(record) =>
                    record.rowType === "GROUP_HEADER"
                      ? "one-line-group-row"
                      : record.rowType === "GRAND_TOTAL"
                        ? "one-line-total-row"
                        : ""
                  }
                />
              </div>
            ) : (
              <Empty className="one-line-empty" description="No preview yet" />
            )}
          </Card>
        </div>
      </section>
    </main>
  );
};

export default OneLineCashFlowReport;
